import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import { buildExpenseRow } from '../utils/accounting';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];
const PAGE = 1000;

export const isMissingExpensesTable = (error) =>
  ['42P01', 'PGRST205'].includes(error?.code) || /relation .*expenses|public\.expenses|table .*expenses/i.test(error?.message || '');

// Las reglas de la base ya vienen en español; un rechazo de permisos viene en inglés y se cambia por una frase clara.
const explain = (error, fallback) => {
  if (error?.code === '42501') return 'No tienes permiso sobre los gastos de este negocio (solo el dueño o un administrador).';
  return error?.code === '22023' && error?.message ? error.message : fallback;
};

const fetchAll = async (build) => {
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await build().range(from, from + PAGE - 1);
    if (error) return { error };
    rows.push(...(data || []));
    if (!data || data.length < PAGE) return { data: rows };
  }
};

// Gastos y cuentas por pagar del negocio. La base valida todo (supabase/migrations/20261006b_expenses.sql); nada se borra: se anula con motivo.
export const useExpenses = () => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMissing, setTableMissing] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusinessId) { setLoading(false); return; }
    setLoading(true);
    const { data, error } = await fetchAll(() => supabase.from('expenses').select('*').eq('business_id', String(activeBusinessId)).order('expense_date', { ascending: false }).order('created_at', { ascending: false }));
    if (error) {
      setTableMissing(isMissingExpensesTable(error));
      if (!isMissingExpensesTable(error)) toast.error('No se pudieron leer los gastos.');
      setExpenses([]);
    } else {
      setTableMissing(false);
      setExpenses(data);
    }
    setLoading(false);
  }, [activeBusinessId]);

  useEffect(() => { load(); }, [load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  const run = async (request, okMessage, fallback) => {
    const { error } = await request;
    if (error) { toast.error(explain(error, fallback)); return false; }
    if (okMessage) toast.success(okMessage);
    await load();
    return true;
  };

  const createExpense = async (form) => {
    if (guardDemo()) return false;
    return run(supabase.from('expenses').insert(buildExpenseRow(form, activeBusinessId)), form.status === 'pagado' ? 'Gasto registrado' : 'Cuenta por pagar registrada', 'No se pudo guardar el gasto.');
  };

  // Corrige textos (proveedor, referencia, notas, categoría, descripción); en uno por pagar también el monto, la fecha y el vencimiento.
  const updateExpense = async (id, patch) => {
    if (guardDemo()) return false;
    return run(supabase.from('expenses').update(patch).eq('id', id).eq('business_id', String(activeBusinessId)), 'Cambios guardados', 'No se pudo guardar el cambio.');
  };

  const markPaid = async (id, { payment_method, paid_at }) => {
    if (guardDemo()) return false;
    return run(supabase.from('expenses').update({ status: 'pagado', payment_method, paid_at: paid_at || null }).eq('id', id).eq('business_id', String(activeBusinessId)), 'Pago registrado', 'No se pudo registrar el pago.');
  };

  const voidExpense = async (id, reason) => {
    if (guardDemo()) return false;
    return run(supabase.from('expenses').update({ status: 'anulado', void_reason: String(reason || '').trim() }).eq('id', id).eq('business_id', String(activeBusinessId)), 'Gasto anulado', 'No se pudo anular el gasto.');
  };

  return { expenses, loading, tableMissing, isReadOnlyDemo, reload: load, createExpense, updateExpense, markPaid, voidExpense };
};
